import { useSearchParams } from 'react-router-dom';

import {
  variantUrlFromUrl,
  pickBestVariant,
  variantIdFromUrl,
} from '@notino/shared/client-utils';
import {
  VariantDisplayType,
  GetCatalogProductViewQuery,
  Video,
} from '@notino/shared/definitions/types';
import { VariantSelector } from '@notino/web-tracking/dist/types/package/ga4/events/constants';

import { VariantDisplayWithoutDefault } from './model';

type Product = NonNullable<
  GetCatalogProductViewQuery['productDetailByCatalogMasterId']
>;

export const isNotDamaged = (variant: {
  attributes: Product['variants'][number]['attributes'];
}) => (variant.attributes ? !variant.attributes.Damage : true);

export const selectVariant = (
  variants: Product['variants'],
  currentUrl: string
): Product['variants'][number] | undefined => {
  const productUrlPath = variantUrlFromUrl(currentUrl);

  if (!productUrlPath) {
    return pickBestVariant(variants);
  }

  return variants.find(
    (variant) =>
      variant.url?.includes(productUrlPath) ||
      isVariantFromCatalogUrl(variant, currentUrl)
  );
};

export const isVariantFromCatalogUrl = (
  variant: Product['variants'][number],
  pathname: string
) => {
  const variantCatalogId = variantIdFromUrl(pathname);

  return (
    variantCatalogId && Number(variant.catalogId) === Number(variantCatalogId)
  );
};

export const getDisplayType = (
  variantDisplayType: Product['variantDisplayType'],
  searchParams?: URLSearchParams
): VariantDisplayWithoutDefault => {
  const variantSelector = searchParams?.get(
    'variantSelector'
  ) as Product['variantDisplayType'];

  if (
    variantSelector &&
    VariantDisplayType[variantSelector] &&
    variantSelector !== VariantDisplayType.Default
  ) {
    return variantSelector;
  }

  if (variantDisplayType && variantDisplayType !== VariantDisplayType.Default) {
    return variantDisplayType;
  }
  return VariantDisplayType.Tiles;
};

export const useDisplayType = (
  product: Product
): VariantDisplayWithoutDefault => {
  const [searchParams] = useSearchParams();

  return getDisplayType(product.variantDisplayType, searchParams);
};

export const useTrackingDisplayType = (product: Product): VariantSelector => {
  const displayType = useDisplayType(product);

  if (
    product.variants.length <= 1 &&
    displayType !== VariantDisplayType.ColorPicker
  ) {
    return 'None';
  }

  return displayType;
};

const youtubeIframe = (videoId: string, autoplay = false) => `
  <iframe
    width="560"
    height="315"
    src="https://www.youtube-nocookie.com/embed/${videoId}?rel=0&amp;showinfo=0&amp;iv_load_policy=3&amp;autoplay=${
  autoplay ? 1 : 0
}"
    frameborder="0"
    loading="lazy"
    allow="autoplay; encrypted-media"
    allowfullscreen
  ></iframe>`;

const vimeoIframe = (videoId: string, autoplay = false) => `
  <iframe
    width="560"
    height="315"
    src="https://player.vimeo.com/video/${videoId}?dnt=1&autoplay=${
  autoplay ? 1 : 0
}"
    frameborder="0"
    loading="lazy"
    allow="autoplay; encrypted-media"
    allowfullscreen
  ></iframe>`;

export const getVideoIframe = (video: Video, autoplay = false): string => {
  if (!video) {
    return '';
  }

  switch (video.type) {
    case 'Youtube':
      return youtubeIframe(video.id, autoplay);
    case 'Vimeo':
      return vimeoIframe(video.id, autoplay);
    default:
      return '';
  }
};
